import { CheckCircle2, Archive, PlayCircle, RotateCcw, FileDown, Copy, ExternalLink } from 'lucide-react';
import { shortcutFor } from './keymap';

/**
 * Action descriptors for the selected brief. The rail, the compact action
 * bar and the keyboard shortcuts all run actions through these, so a key
 * press is refused for exactly the same reason a button is disabled.
 *
 *   { id, label, icon, run, disabled, reason, keys, busy }
 */
export function buildBriefActions({ brief, status, rules, handlers }) {
  if (!brief) return { triage: [], exports: [], byId: {} };
  const keys = (id) => shortcutFor(id)?.keys;
  const rulesReady = rules.status === 'ready';
  const sigmaCount = rulesReady ? rules.data?.sigma_rules?.length || 0 : 0;
  const yaraCount = rulesReady ? rules.data?.yara_rules?.length || 0 : 0;
  const iocCount = brief.iocs?.length || 0;

  const rulesReason = (kind, count) => {
    if (rules.status === 'loading') return 'Loading detections…';
    if (rules.status === 'error') return "Detections couldn't be loaded.";
    return count ? null : `No ${kind} rules were generated for this brief.`;
  };

  const triage = [
    {
      id: 'triage',
      label: 'Start triage',
      icon: PlayCircle,
      keys: keys('triage'),
      disabled: status === 'triage',
      reason: status === 'triage' ? 'Already in triage.' : null,
      run: () => handlers.changeStatus(brief, 'triage'),
    },
    {
      id: 'ack',
      label: 'Acknowledge',
      icon: CheckCircle2,
      keys: keys('ack'),
      disabled: status === 'acked',
      reason: status === 'acked' ? 'Already acknowledged.' : null,
      run: () => handlers.changeStatus(brief, 'acked', { advance: true }),
    },
    {
      id: 'archive',
      label: 'Archive',
      icon: Archive,
      keys: keys('archive'),
      disabled: status === 'archived',
      reason: status === 'archived' ? 'Already archived.' : null,
      run: () => handlers.changeStatus(brief, 'archived', { advance: true }),
    },
  ];
  if (status !== 'new') {
    triage.push({
      id: 'reset',
      label: 'Move back to New',
      icon: RotateCcw,
      quiet: true,
      run: () => handlers.changeStatus(brief, 'new'),
    });
  }

  const sigmaReason = rulesReason('Sigma', sigmaCount);
  const yaraReason = rulesReason('YARA', yaraCount);
  const iocReason = iocCount ? null : 'No IOCs were published by the sources for this brief.';

  const exports = [
    {
      id: 'sigma',
      label: 'Download Sigma rules',
      count: rulesReady ? sigmaCount : null,
      icon: FileDown,
      keys: keys('sigma'),
      disabled: Boolean(sigmaReason),
      reason: sigmaReason,
      run: () => handlers.exportRules(brief, 'sigma', sigmaCount),
    },
    {
      id: 'yara',
      label: 'Download YARA rules',
      count: rulesReady ? yaraCount : null,
      icon: FileDown,
      keys: keys('yara'),
      disabled: Boolean(yaraReason),
      reason: yaraReason,
      run: () => handlers.exportRules(brief, 'yara', yaraCount),
    },
    {
      id: 'copyIocs',
      label: 'Copy IOCs',
      count: iocCount,
      icon: Copy,
      keys: keys('copyIocs'),
      disabled: Boolean(iocReason),
      reason: iocReason,
      run: () => handlers.copyIocs(brief),
    },
    {
      id: 'downloadIocs',
      label: 'Download IOCs (.txt)',
      icon: FileDown,
      keys: keys('downloadIocs'),
      disabled: Boolean(iocReason),
      reason: iocReason,
      run: () => handlers.downloadIocs(brief),
    },
  ];
  if (brief.source_url) {
    exports.push({
      id: 'source',
      label: 'Open source article',
      icon: ExternalLink,
      href: brief.source_url,
      run: () => window.open(brief.source_url, '_blank', 'noopener,noreferrer'),
    });
  }

  const byId = {};
  [...triage, ...exports].forEach((a) => { byId[a.id] = a; });
  return { triage, exports, byId };
}
